'use es6';

import { List, Map as ImmutableMap, Set as ImmutableSet } from 'immutable';
import { ExcludePointsFromTooltipScopes, Style } from './xyRecords';
import { Order } from '../../field/order';
import { renderToString } from 'react-dom/server';
import { SLINKY } from 'reporting-visualizations/constants/hubStyleTokensColors';
import { number, percent } from 'reporting-data/hydrate/numberFormatter';
import { LINE, AREA } from 'reporting-data/constants/chartTypes';
import { FormatTypes } from '../../formatters/propertyFormatter';
import { getDatasetInsightsBreakdownValue, getDatasetInsightsMetricName, getDatasetInsightsRangeName, getDatasetInsightsType } from 'reporting-data/v2/dataset/datasetGetters';
import { getDatasetInsightsRelatedDatasetName } from 'reporting-data/datasetInsights';
import { DATASET_INSIGHT_TYPES } from 'reporting-data/tsTypes/datasetInsightTypes';
import memoize from 'transmute/memoize';
import { ChangeArrow } from 'foundations-components/transitional/components/ChangeArrow';
import { Text } from 'foundations-components/components/Text';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const mergeDefinedFields = (a = {}, b = {}) => ImmutableMap(a).filter(v => v !== undefined).merge(ImmutableMap(b).filter(v => v !== undefined));
export const copyOrder = (layer, fromChannel, toChannel) => layer.setIn([toChannel, 'order'], Order({
  defined: layer.getIn([fromChannel, 'order', 'defined'])
}));

/** Easily swap all x and y */
export const transposeChart = chartVisualization => {
  return chartVisualization.update('layers', layers => layers.map(layer => layer.set('x', layer.get('y')).set('y', layer.get('x')))).update('style', (style = Style({})) => style.set('x', style.get('y')).set('y', style.get('x')));
};
const delta = memoize(({
  total,
  direction,
  rate
}) => renderToString( /*#__PURE__*/_jsxs(_Fragment, {
  children: [/*#__PURE__*/_jsx("strong", {
    children: number(total)
  }), rate != null && /*#__PURE__*/_jsxs(Text, {
    as: "span",
    marginInlineStart: "300",
    typography: "body-100",
    children: [direction && /*#__PURE__*/_jsx(ChangeArrow, {
      size: "14px",
      use: direction
    }), rate === 0 ? '-' : percent(Math.abs(rate), {
      precision: 2
    })]
  })]
})),
//Custom memoize hash function
({
  total,
  direction,
  rate
}) => `${total}:${direction}:${rate}`);
export const label = ({
  total,
  direction,
  rate
}) => ({
  align: 'right',
  style: {
    color: SLINKY
  },
  useHTML: true,
  verticalAlign: 'top',
  y: -30,
  text: delta({
    total,
    direction,
    rate
  })
});
export const getTotal = (data, field, isAccumulated) => isAccumulated ? data.getIn([data.length - 1, field], 0) : data.reduce((total, dataElement) => total + (dataElement.get(field) || 0), 0);
export const rateOfChange = (previousValue, currentValue) => {
  if (previousValue === undefined || currentValue === undefined) {
    return null;
  }
  if (previousValue === 0) {
    return 0;
  }
  return Math.abs(currentValue - previousValue) / previousValue * 100;
};
export const hasOverlapType = serieType => [LINE, AREA].includes(serieType);
export const findOverlaps = (targetChartPoint, series, raasSeriesMap) => {
  const overlaps = series.reduce((acc, serie) => {
    if (serie && hasOverlapType(serie.options.type.toUpperCase()) && serie.visible) {
      const chartPointMatch = serie.data.find(chartPoint => chartPoint.plotX === targetChartPoint.plotX && chartPoint.plotY === targetChartPoint.plotY);
      return chartPointMatch ? acc.push(raasSeriesMap.getIn([serie.options.id, 'points', chartPointMatch.index])) : acc;
    }
    return acc;
  }, List());
  return overlaps.size > 1 ? overlaps : List();
};
export const isXCategorical = layer => layer && (layer.getIn(['x', 'type']) === 'category' || layer.getIn(['x', 'type']) === 'scaffold');
export const isSnowflakeMultiMeasureReport = layers => {
  const layer = layers.first();
  const linearChannel = isXCategorical(layer) ? 'y' : 'x';
  return layer && layer.getIn([linearChannel, 'formattingObjectMap']) && layer.getIn([linearChannel, 'formattingObjectMap']).size > 1;
};
export const doMeasuresHaveDifferentFormatting = layers => {
  if (isSnowflakeMultiMeasureReport(layers)) {
    const layer = layers.first();
    const dataset = layer.get('dataset');
    const linearChannel = isXCategorical(layer) ? 'y' : 'x';
    const formattingTypes = layer.getIn([linearChannel, 'formattingObjectMap']).reduce((nextTypeSet, formattingObject, columnAlias) => {
      const property = dataset.properties.get(columnAlias);
      if (formattingObject.type !== FormatTypes.AUTO) {
        if (formattingObject.type === FormatTypes.CURRENCY) {
          return nextTypeSet.add(formattingObject.type + formattingObject.currencyCode);
        }
        return nextTypeSet.add(formattingObject.type);
      }
      if (property) {
        if (property.get('type') === FormatTypes.CURRENCY) {
          return nextTypeSet.add(property.get('type') + property.get('companyCurrencyCode'));
        }
        return nextTypeSet.add(property.get('type'));
      }
      return nextTypeSet.add(columnAlias);
    }, ImmutableSet());
    return formattingTypes.size !== 1;
  }
  return false;
};
const RGB_MAX_VALUE = 255;
const HEX_RADIX = 16;
const MIN_OPACITY = 10;
const MAX_OPACITY = 100;
const percentToHex = p => {
  const intValue = Math.round(p / 100 * RGB_MAX_VALUE); // map percent to nearest integer (0 - 255)
  const hexValue = intValue.toString(HEX_RADIX); // get hexadecimal representation
  return hexValue.padStart(2, '0').toUpperCase(); // format with leading 0 and upper case characters
};
export const applyOpacity = (color, opacity) => {
  if (!opacity || opacity === MAX_OPACITY) {
    return color;
  }
  const withLimits = value => value < MIN_OPACITY ? MIN_OPACITY : value > MAX_OPACITY ? MAX_OPACITY : value;
  const withOpacity = color.concat(percentToHex(withLimits(opacity)));
  return withOpacity;
};

// This is the base 64 representation of the AnomalousDataPointIcon SVG
const AnomalousDataPointBase64 = 'url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iMTAiIGZpbGw9IiNGMjU0NUIiLz4KPGNpcmNsZSBjeD0iMTAiIGN5PSIxMCIgcj0iOS41IiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjRjI1NDVCIi8+CjxjaXJjbGUgY3g9IjEwIiBjeT0iMTAiIHI9IjQuODc1IiBmaWxsPSJ3aGl0ZSIgc3Ryb2tlPSIjRjI1NDVCIiBzdHJva2Utd2lkdGg9IjQiLz4KPC9zdmc+Cg==)';
const INSIGHT_MARKS = {
  ANOMALY_DETECTION: {
    type: 'scatter',
    marker: {
      enabled: true,
      symbol: AnomalousDataPointBase64,
      height: 15,
      width: 15
    },
    // ensures that the anomalous data point mark appears on top of the primary data point
    zIndex: 1
  },
  FORECAST_TRENDLINE: {
    type: 'line',
    marker: {
      enabled: true
    },
    dashStyle: 'ShortDash',
    // ensure forecast line is behind the primary line so that where the lines meet,
    // the point from the primary data is on top of the connecting point on the forecast layer
    zIndex: -1
  },
  LINEAR_TRENDLINE: {
    type: 'spline',
    marker: {
      enabled: false
    },
    dashStyle: 'ShortDash'
  },
  LOGARITHMIC_TRENDLINE: {
    type: 'spline',
    marker: {
      enabled: false
    },
    dashStyle: 'ShortDash'
  }
};
export const buildXYInsightLayerOverrides = (dataset, datasetKey, options = {}) => {
  const metricName = getDatasetInsightsMetricName(dataset);
  const breakdownValue = getDatasetInsightsBreakdownValue(dataset);
  const rangeName = getDatasetInsightsRangeName(dataset);
  const insightType = getDatasetInsightsType(dataset);
  const linkedToDataset = getDatasetInsightsRelatedDatasetName(datasetKey);
  const {
    showDataLabels
  } = options;
  const shouldHideDataLabels = insightType === DATASET_INSIGHT_TYPES.FORECAST_TRENDLINE ? !showDataLabels : true;
  const primaryOverride = Object.assign({
    dataset: datasetKey,
    mark: INSIGHT_MARKS[insightType],
    linkedTo: {
      dataset: linkedToDataset,
      metricName,
      breakdownValue,
      colorCoordinate: true,
      colorOpacity: rangeName ? 100 : 50,
      showInLegend: true
    },
    hideDataLabels: shouldHideDataLabels,
    insightType
  }, insightType !== DATASET_INSIGHT_TYPES.FORECAST_TRENDLINE && {
    excludePointsFromTooltip: {
      scope: ExcludePointsFromTooltipScopes.all
    }
  });
  const rangeOverride = rangeName ? {
    dataset: datasetKey,
    mark: {
      type: 'arearange',
      // ensure area range is behind the primary or forecast line so tooltip
      // highlights correct correct item when hovering
      zIndex: -2
    },
    linkedTo: {
      dataset: datasetKey,
      metricName,
      breakdownValue,
      colorCoordinate: true,
      colorOpacity: 30,
      // enable toggling of the range independent of the points for anomaly detection only
      showInLegend: insightType === 'ANOMALY_DETECTION'
    },
    excludePointsFromTooltip: {
      scope: ExcludePointsFromTooltipScopes.all
    },
    hideDataLabels: true,
    insightType
  } : undefined;
  return Object.assign({
    primary: primaryOverride
  }, rangeOverride ? {
    range: rangeOverride
  } : null);
};

/**
 * Checks if a chart has multiple layers where each layer defines only one axis (x OR y).
 * This occurs when multiple metrics are displayed without a breakdown/category dimension,
 * causing each metric to be rendered as a separate layer with only its value axis defined.
 *
 * @param {Array} initialLayers - Array of chart layers to validate
 * @returns {boolean} Returns true when:
 *   1. There are multiple layers (more than 1)
 *   2. Every layer has exactly one linear axis defined (either x OR y, but not both)
 */
export const isMultipleLayersWithSingleLinearAxis = initialLayers => initialLayers.every(layer => isXCategorical(layer) ? layer.getIn(['y', 'field']) && !layer.getIn(['x', 'field']) : layer.getIn(['x', 'field']) && !layer.getIn(['y', 'field'])) && initialLayers.size > 1;